<template>
  <b-overlay :show="loading" opacity="0.5">
    <add-or-update-drug-modal
      :editing-data="editingData"
      :is-add-active.sync="toggleModal"
      resource="drugs"
      @create="onCreate"
      @update="onUpdate"
      @refetchData="fetchAllData"
    />

    <div>
      <list-table
        :records="records" 
        :columns="tableColumns"
        :show-import-button="can('drugs:CanImport')"
        :total-records="totalRecords"
        :search-filter.sync="searchFilter"
        search-place-holder="Search"
        :current-page-number.sync="currentPage"
        :show-export-button="can('drugs:CanExport')"
        @export="onExport"
        @import="importDrugs"
      >
        <template v-if="can('drugs:CanAddNew')" #action_button>
          <b-button
            v-ripple.400="'rgba(113, 102, 240, 0.15)'"
            variant="primary"
            @click="onAddNew"
          >
            <span class="align-middle text-lg">Add new</span>
          </b-button>
        </template>

        <template #filters>
          <b-row>
            <b-col cols="12" md="2" class="">
              <v-select
                v-model="routeValue" 
                class="filter-select select-size-md"
                placeholder="Route"
                :clearable="true" 
                :close-on-select="true"
                :disabled="loading"
                :options="routeOptions"
                :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                @input="onRouteOptionSelected"
              />
            </b-col>
            <b-col cols="12" md="2" class="">
              <v-select
                v-model="formulationValue" 
                class="filter-select select-size-md"
                placeholder="Formulation"
                :clearable="true" 
                :close-on-select="true"
                :disabled="loading"
                :options="formulationOptions"
                :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                @input="onFormulationOptionSelected"
              />
            </b-col>
            <b-col>
              <v-select
                v-model="concentrationValue" 
                class="filter-select select-size-md"
                placeholder="Concentration"
                :clearable="true" 
                :close-on-select="true"
                :disabled="loading"
                :options="concentrationOptions"
                :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                @input="onConcentrationOptionSelected"
              />
            </b-col>
            <b-col>
              <v-select
                v-model="doseFrequencyValue" 
                class="filter-select select-size-md"
                placeholder="Dose Frequency"
                :clearable="true" 
                :close-on-select="true"
                :disabled="loading"
                :options="doseFrequencyOptions"
                :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                @input="onDoseFreqOptionSelected"
              />
            </b-col>
          </b-row>
        </template>

        <template #cell(status)="data">
          <b-badge
            pill
            :variant="data.item.status === 'active' ? `light-success` : 'light-danger'"
            class="text-capitalize"
          >
            {{ getUserStatusText(data.item.status) }}
          </b-badge>
        </template>

        <template #cell(actions)="data">
          <b-dropdown
            variant="link"
            no-caret
            :right="$store.state.appConfig.isRTL"
          >
            <template #button-content>
              <feather-icon
                icon="MoreVerticalIcon"
                size="16"
                class="align-middle text-body"
              />
            </template>
            
            <b-dropdown-item @click="onEdit(data.item)">
              <feather-icon icon="EditIcon" />
              <span class="align-middle ml-50">Edit</span>
            </b-dropdown-item>

            <b-dropdown-item @click="onDelete(data.item)">
              <feather-icon icon="TrashIcon" class="text-danger" />
              <span class="align-middle ml-50 text-danger">Delete</span>
            </b-dropdown-item>
          </b-dropdown>
        </template>
      </list-table>
      <import-report-modal
        :show-modal.sync="showImportReportModal"
        :import-report="importReport"
        title="Drug Import Report"
      />
    </div>
  </b-overlay>
</template>

<script>
import {
  BOverlay, BCard, BRow, BCol, BAlert, BLink, BTable,
  BMedia, BAvatar, BButton, BFormFile,
  BBadge, BDropdown, BDropdownItem, BPagination,
  BInputGroup, BInputGroupAppend, BFormInput
} from 'bootstrap-vue'

import { get, debounce, cloneDeep } from "lodash"

import vSelect from 'vue-select'
import Ripple from 'vue-ripple-directive'
import FileDownload from "js-file-download";

import ListTable from "@/@core/components/ListTable/ListTable.vue";
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import ImportReportModal from "@core/components/shared/admin/import-report-modal/ImportReportModal.vue";
import AddOrUpdateDrugModal from '@/@core/components/shared/admin/modals/AddOrUpdateDrugModal.vue'

const watchHandler = {
  handler(){
    this.debouncedFetchUsersData()
  }, 
  immediate: false
}

const watchHandlerAndResetPageNumber = {
  handler(){
    this.currentPage = 1
    this.debouncedFetchUsersData()
  }, 
  immediate: false
}


export default {
  components: {
    ListTable,

    BOverlay,
    BCard,
    BTable,
    BRow,
    BCol,
    BAlert,
    BBadge,
    BLink,
    BMedia,
    BAvatar,
    BButton,
    BDropdown,
    BFormFile,
    BFormInput,
    BPagination,
    BInputGroup, 
    BDropdownItem,
    BInputGroupAppend, 
    
    vSelect,
    ImportReportModal,
    AddOrUpdateDrugModal
  },
  directives: {
    Ripple,
  },
  data(){
    return {
      loading: false,
      showFilter: false,
      toggleModal: false,

      searchFilter: null,
      editingData: null,

      /* Filter */
      routeValue: "",
      routeFilter: null,
      routeOptions: [],

      concentrationValue: "",
      concentrationFilter: null,
      concentrationOptions: [],

      formulationValue: "",
      formulationFilter: null,
      formulationOptions: [],

      doseFrequencyValue: "",
      doseFrequencyFilter: null,
      doseFrequencyOptions: [],

      records: [],

      showImportReportModal: false,
      importReport: {
        successfulInserts: 0,
        failedInserts: 0,
        total: 0,
        writeErrors: []
      }, 
      
      tableColumns: [
        { key: 'generic_name', sortable: false, },
        { key: 'brand_name', sortable: false, formatter: val => val || '-', },
        { key: 'route.name', label: 'Route', sortable: false, formatter: val => val || '-', },
        { key: 'formulation.name', label: 'Formulation', sortable: false, formatter: val => val || '-', },
        { key: 'concentration.name', label: 'Concentration', sortable: false, formatter: val => val || '-', },
        { key: 'dose_frequency.name', label: 'Dose Frequency', sortable: false, formatter: val => val || '-', },
        { key: 'status' },
        { key: 'created', sortable: false, formatter: val => `${this.formatDate(val)}`, },
        { key: 'actions' },
      ],
      debouncedFetchUsersData: () => {}
    }
  },
  computed: {
    perPage: {
      get() {
        return this.$store.getters[`navigation/recordsPerPage`];
      },
      set(value){
        this.$store.commit(`navigation/UPDATE_RECORDS_PER_PAGE`, value)
      }
    },
    currentPage: {
      get() {
        return this.$store.getters[`navigation/currentPage`];
      },
      set(value){
        this.$store.commit(`navigation/UPDATE_CURRENT_PAGE_NUMBER`, value)
      }
    },
    totalRecords: {
      get(){
        return this.$store.getters[`navigation/totalRecords`];
      },
      set(value){
        this.$store.commit(`navigation/UPDATE_TOTAL_RECORDS`, value)
      }
    },
  },
  watch: {
    perPage: {
      handler(){
        this.currentPage = 1;
        this.debouncedFetchUsersData()
      }, 
      immediate: false
    },
    currentPage: watchHandler,
    searchFilter: watchHandlerAndResetPageNumber,

    routeValue: watchHandlerAndResetPageNumber,
    routeFilter: watchHandlerAndResetPageNumber,
    formulationValue: watchHandlerAndResetPageNumber,
    formulationFilter: watchHandlerAndResetPageNumber,
    concentrationValue: watchHandlerAndResetPageNumber,
    concentrationFilter: watchHandlerAndResetPageNumber,
    doseFrequencyValue: watchHandlerAndResetPageNumber,
    doseFrequencyFilter: watchHandlerAndResetPageNumber,
  },
  created(){
    this.debouncedFetchUsersData = debounce(this.fetchAllData, 500);
    this.fetchAllData()
    this.fetchDrugData()

    const { 
      page = 1, 
      search = "", 
      route_id = null,
      concentration_id = null,
      dose_freq_id = null,
      formulation_id = null,
      limit = this.perPage 
    } = this.$route.query;

    this.currentPage = +page;
    this.searchFilter = search;
    this.routeFilter = route_id;
    this.formulationFilter = formulation_id;
    this.doseFrequencyFilter = dose_freq_id;
    this.concentrationFilter = concentration_id;
    this.perPage = +limit;
  },
  methods: {
    async fetchAllData() {
      try {
        this.loading = true; 

        const query = {
          limit: this.perPage,
          page: this.currentPage,
          search: this.searchFilter,
        }
        if (this.routeValue) {
          query.route_id = this.routeFilter;
        }
        if (this.concentrationValue) {
          query.concentration_id = this.concentrationFilter
        }
        if (this.doseFrequencyValue) {
          query.dose_freq_id = this.doseFrequencyFilter
        }
        if (this.formulationValue) {
          query.formulation_id = this.formulationFilter
        }
        this.$router.push({ query }).catch(() => {});

        const request = await this.useJwt().mainService.drugs.list(query);
        const { data, pagination } = request.data;

        this.totalRecords = pagination.totalRecords
        this.perPage = pagination.limit;
        this.records = data;
      } catch (error) {
        const error_message = get(error, "response.data.message") || error.message
        console.log("error_message", error_message)
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Error!',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
            text: error_message
          },
        });
      } finally {
        this.loading = false;
      }
    },
    async fetchDrugData(){
      this.loading = true; 

      const query = {
        limit: this.perPage,
        page: this.currentPage,
        search: this.searchFilter,
      }

      const drug_route_request = await this.useJwt().mainService.drugsRoute.list(query);
      const drug_route_data = drug_route_request.data.data;

      const drug_concentration_request = await this.useJwt().mainService.drugConcentration.list(query);
      const drug_concentration_data = drug_concentration_request.data.data;

      const drug_dose_frequency_request = await this.useJwt().mainService.drugDoseFrequency.list(query);
      const drug_dose_frequency_data = drug_dose_frequency_request.data.data;

      const drug_formulation_request = await this.useJwt().mainService.drugFormulation.list(query);
      const drug_formulation_data = drug_formulation_request.data.data;

      this.routeOptions = drug_route_data.map(item => ({ label: item.name, value: item._id }));
      this.formulationOptions = drug_formulation_data.map(item => ({ label: item.name, value: item._id }));
      this.concentrationOptions = drug_concentration_data.map(item => ({ label: item.name, value: item._id }));
      this.doseFrequencyOptions = drug_dose_frequency_data.map(item => ({ label: item.name, value: item._id }));
    },
    async onExport(){
      try {
        this.loading = true;

        const result = await this.$swal({
          title: 'Confirm Export?',
          text: "This will export data based on current filters.",
          icon: 'warning',
          showCancelButton: true,
          confirmButtonText: 'Yes, proceed.',
          customClass: {
            confirmButton: 'btn btn-primary',
            cancelButton: 'btn btn-outline-danger ml-1',
          },
          buttonsStyling: false,
        });

        if (!result.value) {
          return;
        }

        const query = {
          search: this.searchFilter,
        }

        const response = await this.useJwt().mainService.drugs.export(query);
        FileDownload(response.data, "drugs.csv");
      } catch (error) {
        const error_message = get(error, "response.statusText") || error.message;

        this.$nextTick(() => {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'Error!',
              icon: 'AlertTriangleIcon',
              variant: 'danger',
              text: error_message
            },
          });
        })
      } finally {
        this.loading = false;
      }
    },
    async onCreate(form_data){
      try {
        this.loading = true;

        const result = await this.$swal({
          title: "Caution!!!",
          text: `Proceed to add new record?`,
          icon: "question",
          showCancelButton: true,
          confirmButtonText: 'Yes',
          customClass: {
            confirmButton: 'btn btn-primary',
            cancelButton: 'btn btn-outline-danger ml-1',
          },
          buttonsStyling: false,
        });

        if (!result.isConfirmed) {
          return;
        }

        const { payload, save_and_new } = form_data;
        await this.useJwt().mainService.drugs.create(payload);
        this.fetchAllData();

        if (save_and_new){
          this.toggleModal = false;
          setTimeout(() => {
            this.toggleModal = true;
          }, 100)
        } else {
          this.toggleModal = false;
        }

        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Success!',
            variant: 'success',
            icon: 'CheckCircleIcon',
            text: `Record added successfully`
          },
        });
      } catch (error) {
        const error_message = get(error, "response.data.message") || error.message
        this.errorMessage = error_message;

        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Error!',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
            text: error_message
          },
        });
      } finally {
        this.loading = false
      }
    },
    async onUpdate(form_data){
      try {
        this.loading = true;

        const result = await this.$swal({
          title: "Caution!!!",
          text: `Proceed to update record?`,
          icon: "question",
          showCancelButton: true,
          confirmButtonText: 'Yes',
          customClass: {
            confirmButton: 'btn btn-primary',
            cancelButton: 'btn btn-outline-danger ml-1',
          },
          buttonsStyling: false,
        });

        if (!result.isConfirmed) {
          return;
        }

        const { id, payload } = form_data;
        await this.useJwt().mainService.drugs.update(id, payload);
        this.toggleModal = false;

        this.fetchAllData();

        
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Success!',
            variant: 'success',
            icon: 'CheckCircleIcon',
            text: `Record updated successfully`
          },
        });
      } catch (error) {
        const error_message = get(error, "response.data.message") || error.message
        this.errorMessage = error_message;

        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Error!',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
            text: error_message
          },
        });
      } finally {
        this.loading = false
      }
    },
    async onDelete(record){
      try {
        this.loading = true;

        const result = await this.$swal({
          title: "Caution!!!",
          text: `Proceed to delete this record?`,
          icon: "question",
          showCancelButton: true,
          confirmButtonText: 'Yes',
          customClass: {
            confirmButton: 'btn btn-primary',
            cancelButton: 'btn btn-outline-danger ml-1',
          },
          buttonsStyling: false,
        });

        if (!result.isConfirmed) {
          return;
        }

        await this.useJwt().mainService.drugs.delete(record.id);
        this.fetchAllData();

        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Success!',
            icon: 'CheckCircleIcon',
            variant: 'success',
            text: `Record deleted successfully`
          },
        });
      } catch (error){
        const error_message = get(error, "response.data.message") || error.message
        this.errorMessage = error_message;

        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Error!',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
            text: error_message
          },
        });
      } finally {
        this.loading = false;
      }
    },
    async importDrugs(file){
      try {
        this.loading = true;
        const formData = new FormData();
        formData.append("drugs_csv_file", file);

        const response = await this.useJwt().mainService.drugs.import(formData);
        const { data } = response.data
        this.importReport = data
        
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Success!',
            icon: 'CheckCircleIcon',
            variant: 'success',
            text: "Drug records import have been processed successfully."
          },
        });
        this.showImportReportModal = true;
        this.fetchAllData();
      } catch (error) {
        const error_message = get(error, "response.data.message") || error.message;

        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Error!',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
            text: error_message
          }
        });
      } finally {
        this.loading = false;
      }
    },
    onAddNew(){
      this.editingData = null;
      this.toggleModal = true;
    },
    onEdit(updating_data){
      this.editingData = cloneDeep(updating_data);
      this.toggleModal = true;
    },
    onRouteOptionSelected(val){
      this.routeValue = val.label
      this.routeFilter = val.value
      this.fetchAllData()
    },
    onConcentrationOptionSelected(val){
      this.concentrationValue = val.label
      this.concentrationFilter = val.value
      this.fetchAllData()
    },
    onFormulationOptionSelected(val){
      this.formulationValue = val.label
      this.formulationFilter = val.value
      this.fetchAllData()
    },
    onDoseFreqOptionSelected(val){
      this.doseFrequencyValue = val.label
      this.doseFrequencyFilter = val.value
      this.fetchAllData()
    },
  }
}
</script>

<style lang="scss" scoped>
  @import '@core/scss/vue/libs/vue-select.scss';
  .width-100 {
    width: 100px;
  }
  .per-page-selector {
    width: 90px;
  }
</style>

<style lang="scss">
  .loan-list-tb-row {
    cursor: pointer !important;
  }
</style>
